import { useState, useRef, useCallback } from 'react';

export function useRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const actualMimeTypeRef = useRef(''); // Track the actual MIME type used

  const isSupported = useCallback(() => {
    return !!(navigator.mediaDevices && 
              navigator.mediaDevices.getUserMedia && 
              window.MediaRecorder);
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSupported()) {
      setError('Recording is not supported in this browser');
      return false;
    }

    try {
      setError(null);
      setAudioBlob(null);
      chunksRef.current = [];
      actualMimeTypeRef.current = '';

      // Disable all audio processing to prevent flanger/phase issues
      // This gives raw, unprocessed audio - better for music recording
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        } 
      });
      
      streamRef.current = stream;

      // Determine best supported format
      // iOS Safari only supports audio/mp4, so test in order of preference
      let mimeType = '';
      const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
      ];
      for (const candidate of candidates) {
        if (MediaRecorder.isTypeSupported(candidate)) {
          mimeType = candidate;
          break;
        }
      }
      // If none matched, leave empty and let browser choose

      const options = mimeType ? { mimeType, audioBitsPerSecond: 128000 } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      // Read back the actual MIME type the browser is using
      // This is critical for iOS where the requested type may differ from actual
      actualMimeTypeRef.current = mediaRecorder.mimeType || mimeType || 'audio/mp4';
      console.log('[useRecorder] Requested MIME:', mimeType || '(browser default)');
      console.log('[useRecorder] Actual MIME:', actualMimeTypeRef.current);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Use the actual MIME type, not the requested one
        // This prevents the webm-label-on-mp4-data mismatch that causes
        // garbled playback on iOS
        const finalMimeType = actualMimeTypeRef.current;
        
        const blob = new Blob(chunksRef.current, { type: finalMimeType });
        console.log('[useRecorder] Created blob:', finalMimeType, 'size:', blob.size, 'chunks:', chunksRef.current.length);
        setAudioBlob(blob);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.onerror = (e) => {
        setError('Recording error: ' + (e.error?.message || 'Unknown error'));
        stopRecording();
      };

      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setIsPaused(false);
      
      // Start timer
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 100);

      return true;
    } catch (err) {
      console.error('Failed to start recording:', err);
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError('Failed to start recording: ' + err.message);
      }
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // Request a final data flush before stopping
      // This ensures the last chunk of audio is captured in ondataavailable
      // before onstop fires and creates the blob.
      // Without this, the tail end of the recording can be lost on some 
      // browsers (especially iOS Safari) where onstop races ahead of 
      // the final ondataavailable event.
      try {
        recorder.requestData();
      } catch (e) {
        // requestData() can throw if recorder is in a weird state; ignore
        console.warn('[useRecorder] requestData() failed:', e);
      }
      recorder.stop();
    }

    setIsRecording(false);
    setIsPaused(false);
  }, []);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      
      // Resume timer
      const elapsed = duration * 1000;
      startTimeRef.current = Date.now() - elapsed;
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 100);
    }
  }, [duration]);

  const resetRecording = useCallback(() => {
    setAudioBlob(null);
    setDuration(0);
    setError(null);
    chunksRef.current = [];
  }, []);

  const getAudioURL = useCallback(() => {
    if (audioBlob) {
      return URL.createObjectURL(audioBlob);
    }
    return null;
  }, [audioBlob]);

  return {
    isRecording,
    isPaused,
    audioBlob,
    duration,
    error,
    isSupported: isSupported(),
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    getAudioURL
  };
}

export default useRecorder;
